import * as Redux from '@reduxjs/toolkit';
import { find } from 'lodash';
import { LOCATION_CHANGE, push } from 'react-router-redux';
import { IMaintainedByType } from '~/Slices/schema';
import type { IData } from '~/components/DataTable';
import { fetch2, unwrapFetch2 } from '~/fetch';
import * as Hub from '~/ogm-hub';
import * as FetchAction from '../actions/FetchActionGeneric';
import { sendVirtualPageView } from '../analytics';
import type * as AppState from '../appstate';
import * as Domain from '../domain';
import { ManageMetaProducts, User } from '../helpers';
import { clone } from '../helpers/CommonUtils';
import { customCategoriesEnabled } from '../helpers/Customer';
import { endpoint } from '../helpers/Endpoints';
import Routes, { route } from '../helpers/Routes';
import { getSession, setSession } from '../helpers/Session';
import { toTitleCase } from '../helpers/String';
import { toastr } from '../helpers/Toastr';
import { canEditMasterItem, setUserDefaultPageSize } from '../helpers/User';
import * as GeneralReducers from '../reducers/GeneralReducers';
import { DeleteMetaProducts } from '../remote/hubs/Web_Controllers_V3_MetaProductController';
import { getCoveragePrefs } from '../session.hub';
import type { IModalState } from '../uistate';
import type {
	IBlueprintCoverageStatistics,
	IMetaProductItemLookup,
} from '../values';
import type { StockTypeCoverage } from './util';
import { getStockTypes } from './util';
import { MasterItemType } from './SelectMasterItemTypeModal';

export interface IMetaProductListingState {
	metaProducts: AppState.IResultSet<Domain.IMetaProduct[]>;
	blueprintCoverageStatistics: AppState.ILoader<IBlueprintCoverageStatistics>;
	blueprint: AppState.ILoader<Domain.IBlueprint>;
	currentPage: number;
	itemsPerPage: number;
	categorizeModal: IModalState;
	editAiSettingModal: {
		isOpen: boolean;
		isProcessing: boolean;
		maintainedBy: IMaintainedByType;
	};
	deleteModal: {
		isOpen: boolean;
		isProcessing: boolean;
		masterItemsBeingDeleted: string[];
		requestArgs: Hub.DeleteMetaProductsArgs | null;
	};
	selectMasterItemTypeModal: {
		show: boolean;
		selectedType: string;
	};
	canDelete: boolean;
	deletingMetaProduct: AppState.IAsynchronousAction;
	savingCustomCategoryModal: AppState.IAsynchronousAction;
	requestLocationLevelPricingReport: AppState.IAsynchronousAction;
	selectedMetaProducts: Domain.IMetaProduct[];
	filterCriteria: Domain.IMetaProductListingFilterCriteria;
	customCategories: AppState.ILoader<Domain.ICustomCategory[]>;
	productDetails: AppState.ILoader<Domain.IProductDetails>;
	nutritionalInformation: AppState.ILoader<Hub.NutritionResponse>;
	metaProductItemLookup: AppState.ILoader<IMetaProductItemLookup>;
	stockTypeCoverage: StockTypeCoverage;
	siteTypeId: number;
	allSelected: boolean;
	showLocationLevelPricing: boolean;
	tagSearch: {
		category: { options: { value: string; label: string }[]; loading: boolean };
		primary: { options: { value: string; label: string }[]; loading: boolean };
		packaging: {
			options: { value: string; label: string }[];
			loading: boolean;
		};
		general: { options: { value: string; label: string }[]; loading: boolean };
	};
	selectedTagLabels: Record<string, string>;
}

export interface IMetaProductData extends IData {
	metaProduct: Domain.IMetaProduct;
	locationCoverage: JSX.Element;
	distributionCenterCoverage: JSX.Element;
	metaProductName: JSX.Element;
	aiMode: JSX.Element;
	isSelectByUm: JSX.Element;
	isDynamic: JSX.Element;
	isOnMenu: JSX.Element;
}

export function makeInitialState(): IMetaProductListingState {
	return {
		metaProducts: {
			payload: [],
			inProcess: false,
			error: false,
			totalResults: 0,
			totalPages: 0,
		},
		selectMasterItemTypeModal: {
			show: false,
			selectedType: MasterItemType.Dynamic,
		},
		blueprint: {
			payload: null,
			inProcess: false,
			error: false,
		},
		blueprintCoverageStatistics: {
			payload: null,
			inProcess: false,
			error: false,
		},
		currentPage: 1,
		itemsPerPage: User.getUserDefaultPageSize(),
		categorizeModal: {
			isOpen: false,
		},
		editAiSettingModal: {
			isOpen: false,
			isProcessing: false,
			maintainedBy: null,
		},
		deleteModal: {
			isOpen: false,
			isProcessing: false,
			masterItemsBeingDeleted: [],
			requestArgs: null,
		},
		deletingMetaProduct: {
			inProcess: false,
			error: false,
		},
		savingCustomCategoryModal: {
			inProcess: false,
			error: false,
		},
		requestLocationLevelPricingReport: {
			inProcess: false,
			error: false,
		},
		selectedMetaProducts: [],
		filterCriteria: {
			coverageFilterFlag: false,
			phaseOutFilterFlag: false,
			metaProductHardSpecIndicatorFilterFlag: false,
			metaProductRequiredSpecIndicatorFilterFlag: false,
			metaProductOpenSpecIndicatorFilterFlag: false,
			metaProductUnassignedSpecIndicatorFilterFlag: false,
			metaProductAutoAiSettingFilterFlag: false,
			metaProductManualAiSettingFilterFlag: false,
			metaProductOffAiSettingFilterFlag: false,
			metaProductAutoReprioritizeAiSettingFilterFlag: false,
			metaProductIsSelectByUmEnabledFilterFlag: false,
			metaProductIsSelectByUmDisabledFilterFlag: false,
			metaProductItemHardSpecIndicatorFilterFlag: false,
			metaProductItemRequiredSpecIndicatorFilterFlag: false,
			metaProductItemOpenSpecIndicatorFilterFlag: false,
			metaProductItemUnassignedSpecIndicatorFilterFlag: false,
			metaProductDynamicMasterItemFilterFlag: false,
			metaProductManualMasterItemFilterFlag: false,
			metaproductIsOnMenuFilterFlag: false,
			filterText: null,
			customCategoryIds: [],
			categoryTagId: null,
			primaryTagId: null,
			packagingTagId: null,
			generalTagIds: [],
		},
		customCategories: {
			inProcess: false,
			error: false,
			payload: null,
		},
		productDetails: {
			payload: null,
			inProcess: false,
			error: false,
		},
		nutritionalInformation: {
			payload: null,
			inProcess: false,
			error: false,
		},
		metaProductItemLookup: {
			payload: null,
			inProcess: false,
			error: false,
		},
		stockTypeCoverage: {
			stocked: {
				isSelected: true,
			},
			demand: {
				isSelected: true,
			},
			remote: {
				isSelected: true,
			},
			nonstock: {
				isSelected: true,
				isVisible: false,
			},
			showCoverageFilterNotification: false,
		},
		siteTypeId: -1,
		canDelete: false,
		allSelected: false,
		showLocationLevelPricing: false,
		tagSearch: {
			category: { options: [], loading: false },
			primary: { options: [], loading: false },
			packaging: { options: [], loading: false },
			general: { options: [], loading: false },
		},
		selectedTagLabels: {},
	};
}

// =================================== //
//                                     //
//   ####  ####   ####   #####  ###### //
//  ##  #   ##     ##   ##   #   ##  # //
//  #       ##     ##   ##       ## #  //
//  ###     ##     ##  ##        ####  //
//   ###   ##     ##   ##       ##  #  //
//     #   ##     ##   ##       ##     //
// #  ##   ##  #  ##   ##   #   ##  #  //
// ####   ###### ####   ####   ######  //
//                                     //
// =================================== //

export const slice = Redux.createSlice({
	name: 'metaProductListing',
	initialState: makeInitialState(),

	reducers: {
		fetchLocationLevelPricingEnabledSuccess(
			state,
			action: Redux.PayloadAction<{
				supplierId: number;
				locationLevelPricingEnabled: boolean;
			}>
		) {
			state.showLocationLevelPricing =
				action.payload.locationLevelPricingEnabled;
		},
		fetchLocationLevelPricingEnabledError(state) {
			state.showLocationLevelPricing = false;
		},
		onDeleteClicked(state) {
			const masterItemsBeingDeleted = state.selectedMetaProducts
				.sort((a, b) => a.name.localeCompare(b.name))
				.map((x) => ({
					name: x.name,
					items: x.itemCount === 1 ? '1 Item' : `${x.itemCount} Items`,
				}))
				.map((x) => `${x.name} (${x.items})`);

			state.deleteModal = {
				isOpen: true,
				isProcessing: false,
				requestArgs: null,
				masterItemsBeingDeleted,
			};
		},
		onDeleteCancelled(state) {
			state.deleteModal = {
				isOpen: false,
				isProcessing: false,
				requestArgs: null,
				masterItemsBeingDeleted: [],
			};
		},
		deleteConfirmedPending(state) {
			const specIndicators = checkedSpecIndicators(state.filterCriteria);
			const aiSettingFilters = checkedAiSettings(state.filterCriteria);

			const requestArgs: Hub.DeleteMetaProductsArgs = {
				blueprintId: state.blueprint.payload.id,
				metaProductIds: state.selectedMetaProducts.map((x) => x.id),
				customCategoryIds: state.filterCriteria.customCategoryIds,
				filterText: state.filterCriteria.filterText,
				hasCoverageAlerts: state.filterCriteria.coverageFilterFlag,
				hasPhaseOuts: state.filterCriteria.phaseOutFilterFlag,
				metaProductItemSpecIndicators:
					specIndicators.metaProductItemSpecIndicators,
				metaProductSpecIndicators: specIndicators.metaProductItemSpecIndicators,
				maintainedBys: aiSettingFilters as number[],
				pageIndex: state.currentPage,
				pageSize: state.itemsPerPage,
				stockTypes: [], // TODO Delete this?
			};

			state.deleteModal = {
				isOpen: true,
				isProcessing: true,
				requestArgs,
				masterItemsBeingDeleted: state.deleteModal.masterItemsBeingDeleted,
			};
		},
		deleteConfirmedFailure(state) {
			state.deleteModal = {
				isOpen: true,
				isProcessing: true,
				requestArgs: null,
				masterItemsBeingDeleted: state.deleteModal.masterItemsBeingDeleted,
			};
		},
		deleteConfirmedSuccess(
			state,
			action: Redux.PayloadAction<Hub.DeleteMetaProductsResult>
		) {
			const mappedPayload =
				action.payload.nextMetaProducts.results.map<Domain.IMetaProduct>(
					(x) => {
						return {
							...x,
							items: [],
							selectionRuleSetId: -1,
							coverageRatio: 'NONE',
						};
					}
				);

			state.deleteModal = {
				isOpen: false,
				isProcessing: false,
				requestArgs: null,
				masterItemsBeingDeleted: [],
			};
			state.canDelete = false;
			state.currentPage = action.payload.nextPageIndex;
			state.metaProducts = {
				error: false,
				inProcess: false,
				payload: mappedPayload,
				totalPages: action.payload.nextMetaProducts.totalPages,
				totalResults: action.payload.nextMetaProducts.totalResults,
			};
			state.selectedMetaProducts = [];
			state.allSelected = false;
		},
		onEditAiSettingClicked(state) {
			state.editAiSettingModal = {
				isOpen: true,
				isProcessing: false,
				maintainedBy: 1,
			};
		},
		onEditAiSettingCancelled(state) {
			state.editAiSettingModal = {
				isOpen: false,
				isProcessing: false,
				maintainedBy: 1,
			};
		},
		editAISettingChanged(
			state,
			action: Redux.PayloadAction<IMaintainedByType>
		) {
			state.editAiSettingModal.maintainedBy = action.payload;
		},
		savingAiSetting(state) {
			state.editAiSettingModal.isProcessing = true;
		},
		savingAiSettingSuccess(state) {
			state.selectedMetaProducts = [];
			state.editAiSettingModal = {
				isOpen: false,
				isProcessing: false,
				maintainedBy: 1,
			};
		},
		savingAiSettingError(state) {
			state.selectedMetaProducts = [];
			state.editAiSettingModal = {
				isOpen: false,
				isProcessing: false,
				maintainedBy: 1,
			};
		},
		stockTypeCoverageChanged(
			state,
			action: Redux.PayloadAction<{ stockTypes: string[] }>
		) {
			const selected = action.payload.stockTypes;

			// FIXME BEFORE MR: IMPURE
			const session = getSession();
			const cid = session.customerId;
			const nextSession = clone(session);
			nextSession.metaProductListingOptions.coverageFilters[cid] = selected;
			setSession(nextSession);
			// END FIXME

			const siteTypeId = state.siteTypeId;
			const nextStockTypeCoverage = getStockTypeCoverageState(
				selected,
				siteTypeId,
				state.blueprint.payload.supplierId
			);

			state.stockTypeCoverage = nextStockTypeCoverage;
		},
		loadingMetaProducts(
			state,
			action: Redux.PayloadAction<{ pageNumber: number; itemsPerPage: number }>
		) {
			state.metaProducts = _childReducers.metaProductsReducer(
				state.metaProducts,
				action
			);
			state.currentPage = action.payload.pageNumber;
			state.itemsPerPage = action.payload.itemsPerPage;
		},
		loadingMetaProducts_ERROR(state, action: Redux.PayloadAction) {
			state.metaProducts = _childReducers.metaProductsReducer(
				state.metaProducts,
				action
			);
		},
		loadingMetaProducts_SUCCESS(state, action: Redux.PayloadAction) {
			state.metaProducts = _childReducers.metaProductsReducer(
				state.metaProducts,
				action
			);
			if (state.currentPage > state.metaProducts.totalPages) {
				state.currentPage = 1;
			}
		},
		loadingBlueprint(
			state,
			action: Redux.PayloadAction<{ siteTypeId: number; stockTypes: string[] }>
		) {
			state.stockTypeCoverage = getStockTypeCoverageState(
				action.payload.stockTypes,
				action.payload.siteTypeId,
				'not-loaded'
			);
			state.siteTypeId = action.payload.siteTypeId;
		},
		loadingBlueprint_SUCCESS(
			state,
			action: Redux.PayloadAction<Domain.IBlueprint>
		) {
			state.stockTypeCoverage = getStockTypeCoverageState(
				getStockTypes(state.stockTypeCoverage),
				state.siteTypeId,
				action.payload.supplierId
			);
			state.blueprint = _childReducers.blueprintReducer(
				state.blueprint,
				action
			);
			state.metaProducts = _childReducers.metaProductsReducer(
				state.metaProducts,
				action
			);
		},
		loadingBlueprint_ERROR(state, action: Redux.PayloadAction) {
			state.blueprint = _childReducers.blueprintReducer(
				state.blueprint,
				action
			);
			state.metaProducts = _childReducers.metaProductsReducer(
				state.metaProducts,
				action
			);
		},
		setFilters(
			state,
			action: Redux.PayloadAction<Domain.IMetaProductListingFilterCriteria>
		) {
			state.filterCriteria = action.payload;
		},
		loadingProductDetails(state, action: Redux.PayloadAction) {
			state.productDetails = _childReducers.productDetailsReducer(
				state.productDetails,
				action
			);
			state.nutritionalInformation = {
				payload: null,
				inProcess: false,
				error: false,
			};
		},
		loadingProductDetails_SUCCESS(
			state,
			action: Redux.PayloadAction<Domain.IProductDetails>
		) {
			state.productDetails = _childReducers.productDetailsReducer(
				state.productDetails,
				action
			);
		},
		loadingProductDetails_ERROR(state, action: Redux.PayloadAction) {
			state.productDetails = _childReducers.productDetailsReducer(
				state.productDetails,
				action
			);
		},
		loadingNutritionDetails(state, action: Redux.PayloadAction) {
			state.nutritionalInformation =
				_childReducers.nutritionalInformationReducer(
					state.nutritionalInformation,
					action
				);
		},
		loadingNutirionDetails_ERROR(state, action: Redux.PayloadAction) {
			state.nutritionalInformation =
				_childReducers.nutritionalInformationReducer(
					state.nutritionalInformation,
					action
				);
		},
		loadingNutritionDetails_SUCCESS(
			state,
			action: Redux.PayloadAction<Hub.NutritionResponse>
		) {
			state.nutritionalInformation =
				_childReducers.nutritionalInformationReducer(
					state.nutritionalInformation,
					action
				);
		},
		loadingMetaProductItems(state, action: Redux.PayloadAction) {
			state.metaProductItemLookup = _childReducers.metaProductItemsReducer(
				state.metaProductItemLookup,
				action
			);
		},
		loadingMetaProductItems_ERROR(state, action: Redux.PayloadAction) {
			state.metaProductItemLookup = _childReducers.metaProductItemsReducer(
				state.metaProductItemLookup,
				action
			);
		},
		loadingMetaProductItems_SUCCESS(state, action: Redux.PayloadAction<any>) {
			const metaProductItemLookup = state.metaProductItemLookup.payload
				? { ...state.metaProductItemLookup.payload }
				: {};

			const productListingData = action.payload.products.map((x) => ({
				corpSupplierCode: x.corpSupplierCode,
				supplierProductNumber: x.supplierProductNumber,
				unitMeasureCode: x.unitMeasureCode,
				packSize: find(action.payload.productHeaders, {
					corpSupplierCode: x.corpSupplierCode,
					supplierProductNumber: x.supplierProductNumber,
				}).packSize,
				brand: find(action.payload.productHeaders, {
					corpSupplierCode: x.corpSupplierCode,
					supplierProductNumber: x.supplierProductNumber,
				}).brand,
				description: find(action.payload.productHeaders, {
					corpSupplierCode: x.corpSupplierCode,
					supplierProductNumber: x.supplierProductNumber,
				}).description,
				specIndicatorId: x.specIndicatorId,
			}));

			metaProductItemLookup[action.payload.id] = productListingData;

			return {
				...state,
				metaProductItemLookup: {
					inProcess: false,
					error: false,
					payload: metaProductItemLookup,
				},
			};
		},
		savingCustomCategory(state, action: Redux.PayloadAction) {
			state.savingCustomCategoryModal =
				_childReducers.savingCustomCategoryReducer(
					state.savingCustomCategoryModal,
					action
				);
		},

		savingCustomCategory_ERROR(state, action: Redux.PayloadAction) {
			state.savingCustomCategoryModal =
				_childReducers.savingCustomCategoryReducer(
					state.savingCustomCategoryModal,
					action
				);
		},
		savingCustomCategory_SUCCESS(state, action: Redux.PayloadAction) {
			state.savingCustomCategoryModal =
				_childReducers.savingCustomCategoryReducer(
					state.savingCustomCategoryModal,
					action
				);
		},
		[LOCATION_CHANGE](state, action: Redux.PayloadAction<any>) {
			const newPageUrl = action.payload.pathname;
			if (ManageMetaProducts.locationInMetaProductPages(newPageUrl)) {
				return;
			}

			return makeInitialState();
		},
		addMetaProduct(state, action: Redux.PayloadAction<Domain.IMetaProduct>) {
			const metaProductToAdd = action.payload;

			// Update Selected Meta Products
			const currentNumberProductsOnPageForAdd =
				state.metaProducts.payload.length;
			let newSelectedMetaProductsToAdd = state.selectedMetaProducts;
			const selectedMetaProductIndexToAdd =
				newSelectedMetaProductsToAdd.findIndex((selectedMetaProduct) => {
					return selectedMetaProduct.id === metaProductToAdd.id;
				});

			if (selectedMetaProductIndexToAdd === -1) {
				newSelectedMetaProductsToAdd = state.selectedMetaProducts.concat([
					metaProductToAdd,
				]);
			}
			const newSelectedCountForAdd = newSelectedMetaProductsToAdd.length;

			state.selectedMetaProducts = newSelectedMetaProductsToAdd;
			state.canDelete = newSelectedCountForAdd > 0;
			state.allSelected =
				newSelectedCountForAdd > 0 &&
				newSelectedCountForAdd === currentNumberProductsOnPageForAdd;
		},
		removeMetaProduct(state, action: Redux.PayloadAction<Domain.IMetaProduct>) {
			const metaProductToRemove = action.payload;

			// Update Selected Items
			const newSelectedMetaProducts = state.selectedMetaProducts.splice(0);
			const selectedMetaProductIndex = newSelectedMetaProducts.findIndex(
				(selectedMetaProduct) => {
					return selectedMetaProduct.id === metaProductToRemove.id;
				}
			);
			if (selectedMetaProductIndex > -1) {
				newSelectedMetaProducts.splice(selectedMetaProductIndex, 1);
			}

			const newSelectedCountForRemove = newSelectedMetaProducts.length;
			const currentNumberProductsOnPageForRemove =
				state.metaProducts.payload.length;

			state.selectedMetaProducts = newSelectedMetaProducts;
			state.canDelete = newSelectedCountForRemove > 0;
			state.allSelected =
				newSelectedCountForRemove > 0 &&
				newSelectedCountForRemove === currentNumberProductsOnPageForRemove;
		},
		clearMetaProducts(state) {
			state.selectedMetaProducts = [];
			state.canDelete = false;
			state.allSelected = false;
		},
		clearMetaProductItemLookup(state) {
			state.metaProductItemLookup = makeInitialState().metaProductItemLookup;
		},
		selectAllMetaProducts(state) {
			let selectedMetaProducts = state.selectedMetaProducts;
			const currentMetaProducts = state.metaProducts.payload;

			for (const metaProduct of currentMetaProducts) {
				const alreadySelected = selectedMetaProducts.find(
					(selectedMetaProduct) => {
						return selectedMetaProduct.id === metaProduct.id;
					}
				);

				if (!alreadySelected) {
					selectedMetaProducts = selectedMetaProducts.concat([metaProduct]);
				}
			}

			state.selectedMetaProducts = selectedMetaProducts;
			state.canDelete = selectedMetaProducts.length > 0;
			state.allSelected = true;
		},
		groupMetaProductSuccess(state) {
			state.selectedMetaProducts = [];
		},
		loadingCustomCategories(state, action: Redux.PayloadAction) {
			state.customCategories = _childReducers.customCategoriesReducer(
				state.customCategories,
				action
			);
		},
		loadingCustomCategories_ERROR(state, action: Redux.PayloadAction) {
			state.customCategories = _childReducers.customCategoriesReducer(
				state.customCategories,
				action
			);
		},
		loadingCustomCategories_SUCCESS(
			state,
			action: Redux.PayloadAction<Hub.CustomCategory[]>
		) {
			state.customCategories = _childReducers.customCategoriesReducer(
				state.customCategories,
				action
			);
		},
		onCategorizeClicked(state) {
			state.categorizeModal.isOpen = true;
		},
		categorizeModalCancelled(state) {
			state.categorizeModal.isOpen = false;
		},
		closeDeleteModal(state) {
			state.deleteModal = makeInitialState().deleteModal;
		},
		selectMasterItemTypeModalOpened(state) {
			state.selectMasterItemTypeModal.show = true;
		},
		selectMasterItemTypeModalClosed(state) {
			state.selectMasterItemTypeModal.show = false;
		},
		selectMasterItemTypeModalTypeChanged(state, action) {
			state.selectMasterItemTypeModal.selectedType = action.payload;
		},
		tagSearchLoading(
			state,
			action: Redux.PayloadAction<
				'category' | 'primary' | 'packaging' | 'general'
			>
		) {
			state.tagSearch[action.payload].loading = true;
		},
		tagSearchLoaded(
			state,
			action: Redux.PayloadAction<{
				tagType: 'category' | 'primary' | 'packaging' | 'general';
				options: { value: string; label: string }[];
			}>
		) {
			state.tagSearch[action.payload.tagType].options = action.payload.options;
			state.tagSearch[action.payload.tagType].loading = false;
		},
		tagSearchError(
			state,
			action: Redux.PayloadAction<
				'category' | 'primary' | 'packaging' | 'general'
			>
		) {
			state.tagSearch[action.payload].loading = false;
		},
		setSelectedTagLabel(
			state,
			action: Redux.PayloadAction<{ id: string; label: string }>
		) {
			state.selectedTagLabels[action.payload.id] = action.payload.label;
		},
		removeSelectedTagLabel(state, action: Redux.PayloadAction<string>) {
			delete state.selectedTagLabels[action.payload];
		},
		clearSelectedTagLabels(state) {
			state.selectedTagLabels = {};
		},
	},
});

function checkedSpecIndicators(
	filterCritera: Domain.IMetaProductListingFilterCriteria
) {
	const mp = [];
	if (filterCritera.metaProductUnassignedSpecIndicatorFilterFlag) {
		mp.push(Domain.SpecIndicator.Unassigned);
	}
	if (filterCritera.metaProductOpenSpecIndicatorFilterFlag) {
		mp.push(Domain.SpecIndicator.Open);
	}
	if (filterCritera.metaProductRequiredSpecIndicatorFilterFlag) {
		mp.push(Domain.SpecIndicator.Required);
	}
	if (filterCritera.metaProductHardSpecIndicatorFilterFlag) {
		mp.push(Domain.SpecIndicator.Hard);
	}

	const mpi = [];
	if (filterCritera.metaProductItemUnassignedSpecIndicatorFilterFlag) {
		mpi.push(Domain.SpecIndicator.Unassigned);
	}
	if (filterCritera.metaProductItemOpenSpecIndicatorFilterFlag) {
		mpi.push(Domain.SpecIndicator.Open);
	}
	if (filterCritera.metaProductItemRequiredSpecIndicatorFilterFlag) {
		mpi.push(Domain.SpecIndicator.Required);
	}
	if (filterCritera.metaProductItemHardSpecIndicatorFilterFlag) {
		mpi.push(Domain.SpecIndicator.Hard);
	}

	return {
		metaProductSpecIndicators: mp,
		metaProductItemSpecIndicators: mpi,
	};
}

function checkedAiSettings(
	filterCriteria: Domain.IMetaProductListingFilterCriteria
): IMaintainedByType[] {
	const checkedAiSettingFilters = [];

	if (filterCriteria.metaProductAutoAiSettingFilterFlag) {
		checkedAiSettingFilters.push(IMaintainedByType.OGMAIAuto);
	}

	if (filterCriteria.metaProductManualAiSettingFilterFlag) {
		checkedAiSettingFilters.push(IMaintainedByType.OGMAIManual);
	}

	if (filterCriteria.metaProductOffAiSettingFilterFlag) {
		checkedAiSettingFilters.push(IMaintainedByType.OGM3);
	}

	return checkedAiSettingFilters;
}

function getStockTypeCoverageState(
	selected: string[],
	siteTypeId: number,
	supplierId: number | 'not-loaded'
): StockTypeCoverage {
	// TODO(chrisw): We should have a more reliable way to check which site/supplier is Sysco.
	const isSyscoSite = siteTypeId === 1;
	const isSyscoSupplied = supplierId === 1;

	const nextState: StockTypeCoverage = {
		stocked: { isSelected: selected.indexOf('S') > -1 },
		demand: { isSelected: selected.indexOf('D') > -1 },
		remote: { isSelected: selected.indexOf('R') > -1 },
		nonstock: {
			isSelected: selected.indexOf('N') > -1,
			isVisible: isSyscoSite,
		},
		showCoverageFilterNotification: false,
	};
	nextState.showCoverageFilterNotification =
		shouldShowCoverageFilterNotification(
			nextState,
			isSyscoSite,
			isSyscoSupplied
		);
	return nextState;
}

function shouldShowCoverageFilterNotification(
	coverage: StockTypeCoverage,
	isSyscoSite: boolean,
	isSyscoSupplied: boolean
) {
	if (!isSyscoSupplied) {
		return false;
	}

	if (isSyscoSite) {
		return !(
			coverage.demand.isSelected &&
			coverage.remote.isSelected &&
			coverage.nonstock.isSelected
		);
	}

	return !(coverage.demand.isSelected && coverage.remote.isSelected);
}

// ==================================================== //
//                                                      //
// ######  #### ### #### ###  ###  ###  #### ###   #### //
// # ## #   ##  ##   ##   #    ###  #    ##   #   ##  # //
// # ## #   ##  ##   ##   #    ###  #    ##  #    #     //
//   ##     #####    ##   #    # ## #    ## #     ###   //
//  ##     ##  ##   ##   #    ## ####   ######     ###  //
//  ##     ##  ##   ##   #    #   ##    ##  ##       #  //
//  ##     ##  ##   ##   #    #   ##    ##   ##  #  ##  //
// ####   ### ####   ####    ###   #   ####  ### ####   //
//                                                      //
// ==================================================== //

export type FilterChangedEvent =
	| { type: 'search'; searchTerm: string }
	| { type: 'items-per-page'; value: number }
	| { type: 'page'; index: number }
	| {
			type: 'metaproduct-spec-indicator';
			value: Domain.SpecIndicator;
			isSelected: boolean;
	  }
	| {
			type: 'metaproduct-ai-setting';
			value: IMaintainedByType;
			isSelected: boolean;
	  }
	| {
			type: 'metaproduct-um-selection';
			value: Domain.selectByUmFilterType;
			isSelected: boolean;
	  }
	| {
			type: 'item-spec-indicator';
			value: Domain.SpecIndicator;
			isSelected: boolean;
	  }
	| {
			type: 'dynamic-master-items';
			isSelected: boolean;
	  }
	| {
			type: 'manual-master-items';
			isSelected: boolean;
	  }
	| { type: 'is-on-menu'; isSelected: boolean }
	| { type: 'add-custom-category'; customCategoryId: number }
	| { type: 'remove-custom-category'; customCategoryId: number }
	| { type: 'coverage-alerts'; isSelected: boolean }
	| { type: 'phase-out'; isSelected: boolean }
	| { type: 'hard-spec-phase-out' }
	| { type: 'req-spec-phase-out' }
	| { type: 'hard-spec-coverage-alert' }
	| { type: 'req-spec-coverage-alert' }
	| {
			type: 'tag-filter';
			tagType: 'category' | 'primary' | 'packaging';
			value: number | null;
	  }
	| { type: 'general-tag-filter'; value: number[] }
	| { type: 'clear-tag-filters' };

export const thunks = {
	metaProductListingPageLoaded(args: {
		blueprintId: number;
		searchTerm: string;
		resetFilterCriteria?: boolean;
	}) {
		return async (dispatch, getState) => {
			// TODO(chrisw): clean all this trash up

			let session = getSession();

			if (!session.metaProductListingOptions) {
				setSession({
					...session,
					metaProductListingOptions: {
						coverageFilters: {},
						showItemCoverageWidget: false,
						showItemSpecificationWidget: false,
					},
				});
				session = getSession();
			}

			if (!session.metaProductListingOptions.coverageFilters) {
				const next = clone(session);
				next.metaProductListingOptions.coverageFilters = {};
				setSession(next);
				session = getSession();
			}

			const { blueprintId, searchTerm, resetFilterCriteria } = args;

			dispatch(slice.actions.clearMetaProducts());
			dispatch(slice.actions.clearMetaProductItemLookup());
			dispatch(slice.actions.closeDeleteModal());

			const siteTypeId = (session && session.siteTypeId) || -1;
			const stockTypes = getCoveragePrefs();

			dispatch(slice.actions.loadingBlueprint({ siteTypeId, stockTypes }));
			const bpRes = await Hub.BlueprintController.GetById(blueprintId);

			if (!bpRes.ok) {
				dispatch(slice.actions.loadingBlueprint_ERROR());
				return;
			} else {
				dispatch(slice.actions.loadingBlueprint_SUCCESS(bpRes.value));
			}

			const filterCriteria = resetFilterCriteria
				? ({
						customCategoryIds: [],
				  } as Domain.IMetaProductListingFilterCriteria)
				: ({
						...getState().metaProductListing.filterCriteria,
				  } as Domain.IMetaProductListingFilterCriteria);

			filterCriteria.filterText = searchTerm
				? searchTerm
				: resetFilterCriteria
				? ''
				: filterCriteria.filterText;

			dispatch(
				thunksToRefactor.applyFilterCriteria(
					filterCriteria,
					!resetFilterCriteria
				)
			);

			if (customCategoriesEnabled()) {
				dispatch(slice.actions.loadingCustomCategories());
				const res =
					await Hub.CustomCategoryController.GetCustomCategoriesForCustomer();

				if (!res.ok) {
					dispatch(slice.actions.loadingCustomCategories_ERROR());
				} else {
					dispatch(slice.actions.loadingCustomCategories_SUCCESS(res.value));
				}
			}
		};
	},
	ungroupClicked(data: IMetaProductData) {
		return (dispatch, getState: () => AppState.IAppState) => {
			const state = getState().metaProductListing;
			const blueprintId = state.blueprint.payload.id;

			if (data.metaProduct.isSelectByUm) {
				toastr.warning({
					title: 'Cannot Ungroup Select By U/M Master Item!',
					body: 'You may not ungroup master items in the Select by U/M mode.',
				});
				return;
			}

			dispatch(slice.actions.clearMetaProducts());
			dispatch(slice.actions.addMetaProduct(data.metaProduct));
			dispatch(push(Routes.rearrange(blueprintId)));
		};
	},
	onExportLocationLevelPricingClicked(args: {
		corpSupplierCode: string;
		productNumber: string;
		um: string;
	}) {
		return async (dispatch, getState) => {
			try {
				const state = getState();
				const supplierId =
					state.metaProductListing.blueprint.payload.supplierId;
				const params: Hub.RequestLocationLevelPricingReportArgs = {
					supplierId: supplierId,
					supplierProductNumber: args.productNumber,
					unitOfMeasure: args.um,
				};
				const result = await unwrapFetch2(
					Hub.ReportController.RequestLocationLevelPricingReport(params)
				);
				if (result.errors.length == 0) {
					reportSuccessfullyRequested();
				} else {
					unknownErrorRequestingReport();
				}
			} catch (err: any) {
				if (err.message.includes('report-in-progress')) {
					reportCurrentlyInProgress();
				} else {
					unknownErrorRequestingReport();
				}
			}
		};
	},
	onEditAiSettingConfirmed: () => {
		return async (dispatch, getState: () => AppState.IAppState) => {
			const state = getState().metaProductListing;
			const blueprintId = state.blueprint.payload.id;
			const metaProductIds = state.selectedMetaProducts.map((x) => x.id);
			const maintainedBy = state.editAiSettingModal.maintainedBy as number;

			dispatch(slice.actions.savingAiSetting());
			const editRes =
				await Hub.MetaProductController.EditMetaProductsMaintainedBy({
					blueprintId,
					maintainedBy,
					metaProductIds,
				});

			if (!editRes.ok) {
				dispatch(slice.actions.savingAiSettingError());
				return;
			}

			dispatch(slice.actions.savingAiSettingSuccess());

			dispatch(
				thunksToRefactor.getMetaProducts(
					blueprintId,
					state.currentPage,
					state.itemsPerPage || 1,
					[],
					state.filterCriteria
				)
			);

			dispatch(slice.actions.clearMetaProducts());

			toastr.success({
				title: 'Success!',
				body: 'Master Items updated.',
			});
		};
	},
	onDeleteConfirmed() {
		return async (dispatch, getState) => {
			dispatch(slice.actions.deleteConfirmedPending());
			const stateArgs = getState().metaProductListing.deleteModal.requestArgs;

			const res = await DeleteMetaProducts(stateArgs);

			if (res.ok) {
				dispatch(slice.actions.deleteConfirmedSuccess(res.value));
				toastr.success({
					title: 'Master Items Deleted',
					body: 'The master items were successfully deleted.',
				});
			} else {
				dispatch(slice.actions.deleteConfirmedFailure());
				toastr.error({
					title: 'Error Deleting Master Items',
					body: 'There was a problem deleting the master items. Please refresh the page and try again.',
				});
			}
		};
	},
	onGroupClicked() {
		return (dispatch, getState: () => AppState.IAppState) => {
			const state = getState().metaProductListing;
			const blueprintId = state.blueprint.payload.id;

			const firstType =
				state.selectedMetaProducts[0]?.typeId ?? Hub.MetaProductType.Manual;

			if (firstType === Hub.MetaProductType.Dynamic) {
				const firstUmMode = state.selectedMetaProducts[0].umSelectionModeId;
				const hasUmMismatch = state.selectedMetaProducts.some(
					(mp) => mp.umSelectionModeId !== firstUmMode
				);
				if (hasUmMismatch) {
					toastr.warning({
						title: 'Cannot Group Different U/M Modes!',
						body: 'All dynamic master items must share the same U/M selection mode to be grouped.',
					});
					return Promise.resolve();
				}
			} else {
				for (const metaProduct of state.selectedMetaProducts) {
					if (metaProduct.isSelectByUm) {
						toastr.warning({
							title: 'Cannot Group Select By U/M Master Item!',
							body: 'You may not group master items in Select by U/M mode.',
						});
						return Promise.resolve();
					}
				}
			}

			// Check if all selected meta products have the same type
			if (state.selectedMetaProducts.length > 1) {
				const firstType =
					state.selectedMetaProducts[0].typeId ?? Hub.MetaProductType.Manual;
				const hasTypeMismatch = state.selectedMetaProducts.some(
					(metaProduct) => {
						const currentType =
							metaProduct.typeId ?? Hub.MetaProductType.Manual;
						return currentType !== firstType;
					}
				);

				if (hasTypeMismatch) {
					toastr.warning({
						title: 'Cannot Group Different Master Item Types!',
						body: 'You can only group master items of the same type. Please select only Dynamic or only Manual master items.',
					});
					return Promise.resolve();
				}
			}

			dispatch(push(Routes.rearrange(blueprintId)));
			return Promise.resolve();
		};
	},
	onMetaProductExpanded(args: {
		metaProductData: IMetaProductData;
		isExpanded: boolean;
	}) {
		return async (dispatch, getState: () => AppState.IAppState) => {
			const state = getState().metaProductListing;
			const { metaProductItemLookup, blueprint } = state;
			const { isExpanded, metaProductData } = args;

			const needToFetch =
				!(
					metaProductItemLookup &&
					metaProductItemLookup.payload &&
					metaProductItemLookup.payload[metaProductData.id]
				) && isExpanded;

			if (!needToFetch) {
				return;
			}

			// TODO: Get the hub to generate this correctly by refactoring
			// from query-string attributes to a POSTable body
			dispatch(slice.actions.loadingMetaProductItems());

			const res = await fetch2<Hub.ResultSet<Hub.MetaProductStatistics>>({
				uri: endpoint.metaProducts({
					mpid: metaProductData.metaProduct.id,
					bpid: blueprint.payload.id,
				}),
				method: 'GET',
			});

			// END TODO

			if (!res.ok) {
				dispatch(slice.actions.loadingMetaProductItems_ERROR());
				return;
			}

			dispatch(slice.actions.loadingMetaProductItems_SUCCESS(res.value));
		};
	},
	onMetaProductSelected(args: { row: IMetaProductData; isSelected: boolean }) {
		return (dispatch) => {
			if (canEditMasterItem()) {
				if (args.isSelected) {
					dispatch(slice.actions.addMetaProduct(args.row.metaProduct));
				} else {
					dispatch(slice.actions.removeMetaProduct(args.row.metaProduct));
				}
			}
		};
	},
	onAllMetaProductsSelected(isSelected: boolean) {
		return (dispatch) => {
			if (isSelected) {
				dispatch(slice.actions.selectAllMetaProducts());
			} else {
				dispatch(slice.actions.clearMetaProducts());
			}
		};
	},
	onFilterCriteriaChanged(args: FilterChangedEvent) {
		return (dispatch, getState: () => AppState.IAppState) => {
			// TODO this is too much logic in an action creator
			// Create the request on PENDING and fulfill it from state.
			const state = getState().metaProductListing;
			const blueprintId = state.blueprint.payload?.id;

			switch (args.type) {
				case 'page': {
					dispatch(
						thunksToRefactor.getMetaProducts(
							blueprintId,
							args.index,
							state.itemsPerPage,
							getStockTypes(state.stockTypeCoverage),
							state.filterCriteria
						)
					);
					break;
				}
				case 'items-per-page': {
					setUserDefaultPageSize(args.value);
					dispatch(
						thunksToRefactor.search(state.filterCriteria, 1, args.value)
					);
					break;
				}
				case 'add-custom-category': {
					const customCategoryIds = state.filterCriteria.customCategoryIds;

					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						customCategoryIds: [...customCategoryIds, +args.customCategoryId],
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'remove-custom-category': {
					const nextIds = state.filterCriteria.customCategoryIds.filter(
						(x) => x !== args.customCategoryId
					);

					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						customCategoryIds: nextIds,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'coverage-alerts': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						coverageFilterFlag: args.isSelected,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'phase-out': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						phaseOutFilterFlag: args.isSelected,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'metaproduct-spec-indicator': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
					};

					switch (args.value) {
						case Domain.SpecIndicator.Hard:
							filterCriteria.metaProductHardSpecIndicatorFilterFlag =
								args.isSelected;
							break;
						case Domain.SpecIndicator.Required:
							filterCriteria.metaProductRequiredSpecIndicatorFilterFlag =
								args.isSelected;
							break;
						case Domain.SpecIndicator.Open:
							filterCriteria.metaProductOpenSpecIndicatorFilterFlag =
								args.isSelected;
							break;
						case Domain.SpecIndicator.Unassigned:
							filterCriteria.metaProductUnassignedSpecIndicatorFilterFlag =
								args.isSelected;
					}

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'metaproduct-ai-setting': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
					};

					switch (args.value) {
						case IMaintainedByType.OGMAIAuto:
							filterCriteria.metaProductAutoAiSettingFilterFlag =
								args.isSelected;
							break;
						case IMaintainedByType.OGMAIManual:
							filterCriteria.metaProductManualAiSettingFilterFlag =
								args.isSelected;
							break;
						case IMaintainedByType.OGMAIAutoReprioritize:
							filterCriteria.metaProductAutoReprioritizeAiSettingFilterFlag =
								args.isSelected;
							break;
						case IMaintainedByType.OGM3:
							filterCriteria.metaProductOffAiSettingFilterFlag =
								args.isSelected;
							break;
					}

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'item-spec-indicator': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
					};

					switch (args.value) {
						case Domain.SpecIndicator.Hard:
							filterCriteria.metaProductItemHardSpecIndicatorFilterFlag =
								args.isSelected;
							break;
						case Domain.SpecIndicator.Required:
							filterCriteria.metaProductItemRequiredSpecIndicatorFilterFlag =
								args.isSelected;
							break;
						case Domain.SpecIndicator.Open:
							filterCriteria.metaProductItemOpenSpecIndicatorFilterFlag =
								args.isSelected;
							break;
						case Domain.SpecIndicator.Unassigned:
							filterCriteria.metaProductItemUnassignedSpecIndicatorFilterFlag =
								args.isSelected;
					}

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'req-spec-phase-out': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						phaseOutFilterFlag: true,
						metaProductItemRequiredSpecIndicatorFilterFlag: true,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'hard-spec-phase-out': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						phaseOutFilterFlag: true,
						metaProductItemHardSpecIndicatorFilterFlag: true,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'req-spec-coverage-alert': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						coverageFilterFlag: true,
						metaProductRequiredSpecIndicatorFilterFlag: true,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'hard-spec-coverage-alert': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						coverageFilterFlag: true,
						metaProductHardSpecIndicatorFilterFlag: true,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'search': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						filterText: args.searchTerm,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'metaproduct-um-selection': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
					};

					switch (args.value) {
						case Domain.selectByUmFilterType.Enabled:
							filterCriteria.metaProductIsSelectByUmEnabledFilterFlag =
								args.isSelected;
							break;
						case Domain.selectByUmFilterType.Disabled:
							filterCriteria.metaProductIsSelectByUmDisabledFilterFlag =
								args.isSelected;
							break;
					}

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'dynamic-master-items': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
					};

					filterCriteria.metaProductDynamicMasterItemFilterFlag =
						args.isSelected;
					if (args.isSelected) {
						filterCriteria.metaProductManualMasterItemFilterFlag = false;
					}

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'manual-master-items': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
					};

					filterCriteria.metaProductManualMasterItemFilterFlag =
						args.isSelected;
					if (args.isSelected) {
						filterCriteria.metaProductDynamicMasterItemFilterFlag = false;
					}

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'is-on-menu': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
					};

					filterCriteria.metaproductIsOnMenuFilterFlag = args.isSelected;

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'tag-filter': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						[`${args.tagType}TagId`]: args.value,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'general-tag-filter': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						generalTagIds: args.value,
					};

					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
				case 'clear-tag-filters': {
					const filterCriteria: Domain.IMetaProductListingFilterCriteria = {
						...state.filterCriteria,
						categoryTagId: null,
						primaryTagId: null,
						packagingTagId: null,
						generalTagIds: [],
					};

					dispatch(slice.actions.clearSelectedTagLabels());
					dispatch(thunksToRefactor.applyFilterCriteria(filterCriteria));
					break;
				}
			}

			return Promise.resolve({});
		};
	},
	searchTags(args: {
		tagType: 'category' | 'primary' | 'packaging' | 'general';
		searchTerm: string;
	}) {
		return async (dispatch) => {
			const hubTagType = {
				category: Hub.TagType.Category,
				primary: Hub.TagType.Primary,
				packaging: Hub.TagType.Packaging,
				general: Hub.TagType.General,
			}[args.tagType];

			dispatch(slice.actions.tagSearchLoading(args.tagType));

			try {
				const response = await Hub.TagController.SearchTags({
					searchTerm: args.searchTerm,
					tagType: [hubTagType],
					orderBy: Hub.TagOrder.Alphabetical,
					descending: false,
					skip: 0,
					take: 50,
					includeEmbedding: false,
				});

				if (!response.ok) {
					dispatch(slice.actions.tagSearchError(args.tagType));
					return;
				}

				const options = response.value.tags.map((tag) => ({
					value: tag.id.toString(),
					label: toTitleCase(tag.name),
				}));

				dispatch(
					slice.actions.tagSearchLoaded({ tagType: args.tagType, options })
				);
			} catch {
				dispatch(slice.actions.tagSearchError(args.tagType));
			}
		};
	},
	onCategorizeConfirmed(customCategoryId: number) {
		return async (dispatch, getState: () => AppState.IAppState) => {
			const state = getState().metaProductListing;
			const blueprintId = state.blueprint.payload.id;
			const metaProductIds = state.selectedMetaProducts.map((x) => x.id);

			dispatch(slice.actions.savingCustomCategory());

			const editRes = await Hub.MetaProductController.EditMetaProductsCategory({
				blueprintId,
				customCategoryId,
				metaProductIds,
			});

			if (!editRes.ok) {
				dispatch(slice.actions.savingCustomCategory_ERROR());
				return;
			}

			dispatch(slice.actions.savingCustomCategory_SUCCESS());

			dispatch(slice.actions.categorizeModalCancelled());

			dispatch(
				thunksToRefactor.getMetaProducts(
					blueprintId,
					state.currentPage || 1,
					state.itemsPerPage,
					[],
					state.filterCriteria
				)
			);

			dispatch(slice.actions.clearMetaProducts());

			toastr.success({
				title: 'Success!',
				body: 'Master Items updated.',
			});
		};
	},
	navigateToNewMasterItemPage(isManual: boolean) {
		return async (dispatch, getState: () => AppState.IAppState) => {
			dispatch(slice.actions.selectMasterItemTypeModalClosed());
			const state = getState();
			const blueprintId = state.metaProductListing.blueprint.payload.id;
			const dynamic =
				isManual === true
					? false
					: state.metaProductListing.selectMasterItemTypeModal.selectedType ===
					  MasterItemType.Dynamic;
			dispatch(push(route.prioritize(blueprintId.toString(), null, dynamic)));
		};
	},
};

// =================================================================== //
//                                                                     //
//  ######   ######  ######     ##      ##### ######   ####   ######   //
//   ##  ##   ##  #   ##  #     ##     ##   # # ## #  ##  ##   ##  ##  //
//   ##  ##   ## #    ## #     ###     ##     # ## #  ##  ##   ##  ##  //
//   #####    ####    ####     # ##   ##        ##   ##   ##   #####   //
//  ## ##    ##  #   ##  #    #  ##   ##       ##    ##   ##  ## ##    //
//  ##  ##   ##      ##       ######  ##       ##    ##  ##   ##  ##   //
//  ##  ##   ##  #   ##      #    ##  ##   #   ##    ##  ##   ##  ##   //
// #### ### ######  ####    ###  ####  ####   ####    ####   #### ###  //
//                                                                     //
// =================================================================== //

// These thunks are a rats nest of self-referencing multi-dispatching.
// must be nuked

const thunksToRefactor = {
	search(
		filterCriteria: Domain.IMetaProductListingFilterCriteria,
		page: number,
		itemsPerPage: number
	) {
		return (dispatch, getState) => {
			sendVirtualPageView(
				`${window.location.pathname}?search=${filterCriteria.filterText}`
			);
			const state = getState().metaProductListing;
			const blueprintId = state.blueprint.payload.id;
			const stockTypeCoverage = state.stockTypeCoverage;
			dispatch(
				thunksToRefactor.getMetaProducts(
					blueprintId,
					page ? page : 1,
					itemsPerPage,
					getStockTypes(stockTypeCoverage),
					filterCriteria
				)
			);
		};
	},
	applyFilterCriteria(
		filterCriteria: Domain.IMetaProductListingFilterCriteria,
		persistPageNumber?: boolean
	) {
		return (dispatch, getState) => {
			dispatch(slice.actions.setFilters(filterCriteria));
			const metaProductListingState = getState().metaProductListing;
			dispatch(
				thunksToRefactor.search(
					filterCriteria,
					persistPageNumber ? metaProductListingState.currentPage : 1,
					metaProductListingState.itemsPerPage
				)
			);
		};
	},
	getMetaProducts(
		blueprintId: number,
		pageNumber: number,
		itemsPerPage: number,
		stockTypes: string[],
		filterCriteria?: Domain.IMetaProductListingFilterCriteria
	) {
		const metaProductSpecIndicators: Domain.SpecIndicator[] = [];
		const metaProductAiSettings: IMaintainedByType[] = [];
		const metaProductItemSpecIndicators: Domain.SpecIndicator[] = [];
		const metaProductIsSelectByUm =
			filterCriteria.metaProductIsSelectByUmEnabledFilterFlag &&
			filterCriteria.metaProductIsSelectByUmDisabledFilterFlag
				? null
				: filterCriteria.metaProductIsSelectByUmEnabledFilterFlag &&
				  !filterCriteria.metaProductIsSelectByUmDisabledFilterFlag
				? true
				: !filterCriteria.metaProductIsSelectByUmEnabledFilterFlag &&
				  filterCriteria.metaProductIsSelectByUmDisabledFilterFlag
				? false
				: null;
		let metaProductType: number | null = null;
		if (filterCriteria.metaProductDynamicMasterItemFilterFlag) {
			metaProductType = Hub.MetaProductType.Dynamic as number;
		} else if (filterCriteria.metaProductManualMasterItemFilterFlag) {
			metaProductType = Hub.MetaProductType.Manual as number;
		}

		if (filterCriteria) {
			if (filterCriteria.metaProductHardSpecIndicatorFilterFlag) {
				metaProductSpecIndicators.push(Domain.SpecIndicator.Hard);
			}
			if (filterCriteria.metaProductRequiredSpecIndicatorFilterFlag) {
				metaProductSpecIndicators.push(Domain.SpecIndicator.Required);
			}
			if (filterCriteria.metaProductOpenSpecIndicatorFilterFlag) {
				metaProductSpecIndicators.push(Domain.SpecIndicator.Open);
			}
			if (filterCriteria.metaProductUnassignedSpecIndicatorFilterFlag) {
				metaProductSpecIndicators.push(Domain.SpecIndicator.Unassigned);
			}
			if (filterCriteria.metaProductAutoAiSettingFilterFlag) {
				metaProductAiSettings.push(IMaintainedByType.OGMAIAuto);
			}
			if (filterCriteria.metaProductAutoReprioritizeAiSettingFilterFlag) {
				metaProductAiSettings.push(IMaintainedByType.OGMAIAutoReprioritize);
			}
			if (filterCriteria.metaProductManualAiSettingFilterFlag) {
				metaProductAiSettings.push(IMaintainedByType.OGMAIManual);
			}
			if (filterCriteria.metaProductOffAiSettingFilterFlag) {
				metaProductAiSettings.push(IMaintainedByType.OGM3);
			}
			if (filterCriteria.metaProductItemHardSpecIndicatorFilterFlag) {
				metaProductItemSpecIndicators.push(Domain.SpecIndicator.Hard);
			}
			if (filterCriteria.metaProductItemRequiredSpecIndicatorFilterFlag) {
				metaProductItemSpecIndicators.push(Domain.SpecIndicator.Required);
			}
			if (filterCriteria.metaProductItemOpenSpecIndicatorFilterFlag) {
				metaProductItemSpecIndicators.push(Domain.SpecIndicator.Open);
			}
			if (filterCriteria.metaProductItemUnassignedSpecIndicatorFilterFlag) {
				metaProductItemSpecIndicators.push(Domain.SpecIndicator.Unassigned);
			}
		}

		const params = {
			bp: blueprintId,
			q: filterCriteria && filterCriteria.filterText,
			cov: filterCriteria && filterCriteria.coverageFilterFlag,
			po: filterCriteria && filterCriteria.phaseOutFilterFlag,
			mpsi: metaProductSpecIndicators,
			mpmb: metaProductAiSettings,
			mpisi: metaProductItemSpecIndicators,
			ccid: filterCriteria && filterCriteria.customCategoryIds,
			isbum: metaProductIsSelectByUm,
			isdyn: metaProductType,
			ismen: filterCriteria && filterCriteria.metaproductIsOnMenuFilterFlag,
			ps: itemsPerPage,
			pi: pageNumber,
			stockTypes,
			tagIds: filterCriteria
				? [
						filterCriteria.categoryTagId,
						filterCriteria.primaryTagId,
						filterCriteria.packagingTagId,
						...(filterCriteria.generalTagIds || []),
				  ].filter(Boolean)
				: [],
		};

		return FetchAction.CreateFetchAction({
			url: endpoint.masterItems(params),
			startActionPayload: {
				pageNumber: pageNumber || 1,
				itemsPerPage,
			},
			startAction: 'metaProductListing/loadingMetaProducts',
		});
	},
};

const unknownErrorRequestingReport = () => {
	toastr.error({
		title: 'Report Request Failed',
		body: 'The report was not successfully requested.',
	});
};
const reportCurrentlyInProgress = () => {
	toastr.warning({
		title: 'Report Request Failed',
		body: 'Another report request is currently in progress',
	});
};
const reportSuccessfullyRequested = () => {
	toastr.success({
		title: 'Report Request Sent',
		body: 'The report was successfully requested.',
	});
};

export const _childReducers = {
	metaProductsReducer: GeneralReducers.CreateResultSetReducer<
		Domain.IMetaProduct[]
	>({
		startAction: 'metaProductListing/loadingMetaProducts',
	}),
	blueprintReducer: GeneralReducers.CreateLoaderReducer<Domain.IBlueprint>({
		startAction: 'metaProductListing/loadingBlueprint',
		clearPayloadWith: makeInitialState().blueprint.payload,
	}),
	savingCustomCategoryReducer: GeneralReducers.CreateAsynchronousActionReducer({
		startAction: 'metaProductListing/savingCustomCategory',
	}),
	customCategoriesReducer: GeneralReducers.CreateLoaderReducer<
		Domain.ICustomCategory[]
	>({
		startAction: 'metaProductListing/loadingCustomCategories',
		clearPayloadWith: makeInitialState().customCategories.payload,
	}),
	productDetailsReducer: GeneralReducers.CreateLoaderReducer({
		startAction: 'metaProductListing/loadingProductDetails',
		clearPayloadWith: makeInitialState().productDetails.payload,
	}),
	nutritionalInformationReducer: GeneralReducers.CreateLoaderReducer({
		startAction: 'metaProductListing/loadingNutritionDetails',
		clearPayloadWith: makeInitialState().nutritionalInformation.payload,
	}),
	metaProductItemsReducer: GeneralReducers.CreateLoaderReducer({
		startAction: 'metaProductListing/loadingMetaProductItems',
		clearPayloadWith: makeInitialState().metaProductItemLookup.payload,
	}),
};
